import { type AnalyticsClient, createAnalyticsClient } from "@lafayette/api-client";
import { createContext, type ReactNode, useContext, useMemo } from "react";

import { currentAccessToken } from "../catalog/admin-runtime";
import { getAdminPublicConfig } from "../config/public-config";

// Product analytics of the admin app (CHT-769). Staff operation telemetry through the Lafayette API: the app holds no vendor key and no
// SDK. It is OFF unless `VITE_LAFAYETTE_ANALYTICS=on` (then no call is made). The server checks the closed taxonomy (`admin_operation_performed`
// with a closed `operation` and the staff role), scans for forbidden data and sends nothing with a customer in it.
// A screen reads the client with `useAdminAnalytics()`; outside a provider it is `null` and a screen sends nothing.

const AdminAnalyticsContext = createContext<AnalyticsClient | null>(null);

export const useAdminAnalytics = (): AnalyticsClient | null => useContext(AdminAnalyticsContext);

export function AdminAnalyticsProvider({
  children,
  client,
}: Readonly<{ children: ReactNode; client?: AnalyticsClient }>) {
  const made = useMemo(() => {
    if (client !== undefined) return client;
    const config = getAdminPublicConfig();
    return createAnalyticsClient({
      baseUrl: config.apiUrl.toString(),
      getAccessToken: currentAccessToken,
      audience: "staff",
      enabled: config.analyticsEnabled,
    });
  }, [client]);
  return <AdminAnalyticsContext.Provider value={made}>{children}</AdminAnalyticsContext.Provider>;
}
